import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, ArrowRight, Cpu, HardDrive, Database, Wifi, Cloud, Globe, Rocket } from 'lucide-react';
import { cloudPackages, cdnPackages, deployPackages } from '../data/mock';

// OPT value in USD
const OPT_VALUE = 0.0012;

// Helper function to convert USD to OPT
const formatOPTPrice = (usdPrice) => {
  const optAmount = Math.round(usdPrice / OPT_VALUE);
  return optAmount.toLocaleString();
};

// Helper function to format USD price (always show 2 decimal places)
const formatUSDPrice = (price) => {
  return price.toFixed(2);
};

const ProductTab = ({ active, onClick, children, color }) => (
  <button
    onClick={onClick}
    className={`px-4 md:px-6 py-3 text-sm md:text-base font-semibold transition-all ${
      active 
        ? `bg-[${color}] text-white` 
        : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
    }`}
    style={active ? { backgroundColor: color } : {}}
  >
    {children}
  </button>
);

const BillingToggle = ({ isAnnual, setIsAnnual }) => (
  <div className="flex items-center justify-center gap-3 mb-10 md:mb-14">
    <span className={`text-sm md:text-base ${!isAnnual ? 'text-white' : 'text-white/50'}`}>Monthly</span>
    <button
      onClick={() => setIsAnnual(!isAnnual)}
      className="relative w-14 h-7 rounded-full transition-colors"
      style={{ backgroundColor: isAnnual ? '#2a5ff6' : 'rgba(255,255,255,0.1)' }}
    >
      <span 
        className="absolute top-1 w-5 h-5 bg-white rounded-full transition-transform duration-300"
        style={{ left: isAnnual ? '30px' : '4px' }}
      />
    </button>
    <span className={`text-sm md:text-base ${isAnnual ? 'text-white' : 'text-white/50'}`}>Annual</span>
    <span className="text-xs md:text-sm text-[#2a5ff6] font-medium ml-1">Save 20%</span>
  </div>
);

// Cloud Package Card
const CloudPackageCard = ({ pkg, isAnnual }) => {
  const price = isAnnual ? pkg.annualPrice : pkg.monthlyPrice;
  
  return (
    <div className={`card-dark flex flex-col p-5 md:p-8 ${pkg.popular ? 'card-popular animate-glow' : ''}`}>
      {pkg.popular && <div className="package-badge" style={{ left: 'auto', right: '16px', transform: 'none' }}>Most Popular</div>}

      <div className="mb-4 md:mb-6">
        <h3 className={`text-xl md:text-2xl font-semibold mb-2 ${pkg.popular ? 'text-[#2a5ff6]' : 'text-white'}`}>
          {pkg.name}
        </h3>
        <p className="text-sm text-white/50">{pkg.tagline}</p>
      </div>

      <div className="mb-6 md:mb-8">
        <div>
          <span className="text-3xl md:text-5xl font-bold text-white">${formatUSDPrice(price)}</span>
          <span className="text-white/50 text-sm md:text-base">/month</span>
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <img src="/optio-icon.png" alt="OPT" className="w-4 h-4 md:w-5 md:h-5 rounded-sm" />
          <span className="text-sm md:text-base text-[#2a5ff6] font-medium">{formatOPTPrice(price)} OPT</span>
          <span className="text-white/40 text-xs">/mo</span>
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <img src="/sovr-icon.png" alt="SOVR" className="w-4 h-4 md:w-5 md:h-5 rounded-sm" />
          <span className="text-xs md:text-sm text-white/40 italic">SOVR coming soon</span>
        </div>
      </div>

      <div className="bg-white/5 p-4 mb-5 md:mb-6 grid grid-cols-2 gap-3 rounded-xl">
        <div className="flex items-center gap-2">
          <Cpu size={14} color="#2a5ff6" />
          <span className="text-xs md:text-sm text-white/70">{pkg.specs.vCPU} vCPU</span>
        </div>
        <div className="flex items-center gap-2">
          <HardDrive size={14} color="#2a5ff6" />
          <span className="text-xs md:text-sm text-white/70">{pkg.specs.memory}</span>
        </div>
        <div className="flex items-center gap-2">
          <Database size={14} color="#2a5ff6" />
          <span className="text-xs md:text-sm text-white/70">{pkg.specs.nvmeStorage} NVMe</span>
        </div>
        <div className="flex items-center gap-2">
          <Wifi size={14} color="#2a5ff6" />
          <span className="text-xs md:text-sm text-white/70">{pkg.specs.cdnBandwidth} CDN</span>
        </div>
      </div>

      <div className="flex-1 mb-6 md:mb-8">
        {pkg.features.map((feature, idx) => (
          <div key={idx} className="flex items-start gap-3 mb-2 md:mb-3">
            <Check size={16} color="#2a5ff6" className="flex-shrink-0 mt-0.5" />
            <span className="text-xs md:text-sm text-white/70">{feature}</span>
          </div>
        ))}
      </div>

      <Link
        to="/contact"
        className={`${pkg.popular ? 'btn-primary' : 'btn-secondary'} w-full justify-center text-sm md:text-base`}
      >
        Get Started
        <ArrowRight size={16} />
      </Link>
    </div>
  );
};

// CDN Package Card
const CDNPackageCard = ({ pkg, isAnnual, accentColor }) => {
  const price = isAnnual ? pkg.annualPrice : pkg.monthlyPrice;
  const isCustom = pkg.custom;
  
  return (
    <div 
      className={`bg-[var(--bg-secondary)] border flex flex-col p-5 md:p-8 transition-all hover:translate-y-[-4px] rounded-2xl ${
        pkg.popular ? 'border-[#3B82F6]' : 'border-white/10 hover:border-[#3B82F6]'
      }`}
      style={pkg.popular ? { boxShadow: '0 0 30px rgba(59, 130, 246, 0.2)' } : {}}
    >
      {pkg.popular && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#3B82F6] text-white px-4 py-1 text-xs font-bold uppercase tracking-wider rounded-full">
          Most Popular
        </div>
      )}

      <div className="mb-4 md:mb-6">
        <h3 className={`text-xl md:text-2xl font-semibold mb-2 ${pkg.popular ? 'text-[#3B82F6]' : 'text-white'}`}>
          {pkg.name}
        </h3>
        <p className="text-sm text-white/50">{pkg.tagline}</p>
      </div>

      <div className="mb-6 md:mb-8">
        {isCustom ? (
          <span className="text-3xl md:text-4xl font-bold text-white">Custom</span>
        ) : (
          <>
            <div>
              <span className="text-3xl md:text-5xl font-bold text-white">${formatUSDPrice(price)}</span>
              <span className="text-white/50 text-sm md:text-base"> /mo</span>
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              <img src="/optio-icon.png" alt="OPT" className="w-4 h-4 md:w-5 md:h-5 rounded-sm" />
              <span className="text-sm md:text-base text-[#3B82F6] font-medium">{formatOPTPrice(price)} OPT</span>
              <span className="text-white/40 text-xs">/mo</span>
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              <img src="/sovr-icon.png" alt="SOVR" className="w-4 h-4 md:w-5 md:h-5 rounded-sm" />
              <span className="text-xs md:text-sm text-white/40 italic">SOVR coming soon</span>
            </div>
          </>
        )}
      </div>

      <div className="flex-1 mb-6 md:mb-8">
        {pkg.features.map((feature, idx) => (
          <div key={idx} className="flex items-start gap-3 mb-2 md:mb-3">
            <Check size={16} color="#3B82F6" className="flex-shrink-0 mt-0.5" />
            <span className="text-xs md:text-sm text-white/70">{feature}</span>
          </div>
        ))}
      </div>

      <Link
        to="/contact"
        className={`w-full justify-center text-sm md:text-base inline-flex items-center gap-2 py-3 px-6 font-semibold transition-colors rounded-xl ${
          pkg.popular || isCustom
            ? 'bg-[#3B82F6] text-white hover:bg-[#2563EB]' 
            : 'bg-transparent border border-white/20 text-white hover:bg-white/10'
        }`}
      >
        {isCustom ? 'Contact Sales' : 'Get Started'}
        <ArrowRight size={16} />
      </Link>
    </div>
  );
};

// Deploy Package Card
const DeployPackageCard = ({ pkg, isAnnual }) => {
  const price = isAnnual ? pkg.annualPrice : pkg.monthlyPrice;
  const isCustom = pkg.custom;
  
  return (
    <div 
      className={`bg-[var(--bg-secondary)] border flex flex-col p-5 md:p-8 transition-all hover:translate-y-[-4px] rounded-2xl ${
        pkg.popular ? 'border-[#F97316]' : 'border-white/10 hover:border-[#F97316]'
      }`}
      style={pkg.popular ? { boxShadow: '0 0 30px rgba(249, 115, 22, 0.2)' } : {}}
    >
      {pkg.popular && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#F97316] text-white px-4 py-1 text-xs font-bold uppercase tracking-wider rounded-full">
          Most Popular
        </div>
      )}

      <div className="mb-4 md:mb-6">
        <h3 className={`text-xl md:text-2xl font-semibold mb-2 ${pkg.popular ? 'text-[#F97316]' : 'text-white'}`}>
          {pkg.name}
        </h3>
        <p className="text-sm text-white/50">{pkg.tagline}</p>
      </div>

      <div className="mb-6 md:mb-8">
        {isCustom ? (
          <span className="text-3xl md:text-4xl font-bold text-white">Custom</span>
        ) : (
          <>
            <div>
              <span className="text-3xl md:text-5xl font-bold text-white">${formatUSDPrice(price)}</span>
              <span className="text-white/50 text-sm md:text-base">{pkg.priceUnit}</span>
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              <img src="/optio-icon.png" alt="OPT" className="w-4 h-4 md:w-5 md:h-5 rounded-sm" />
              <span className="text-sm md:text-base text-[#F97316] font-medium">{formatOPTPrice(price)} OPT</span>
              <span className="text-white/40 text-xs">/mo</span>
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              <img src="/sovr-icon.png" alt="SOVR" className="w-4 h-4 md:w-5 md:h-5 rounded-sm" />
              <span className="text-xs md:text-sm text-white/40 italic">SOVR coming soon</span>
            </div>
          </>
        )}
      </div>

      <div className="flex-1 mb-6 md:mb-8">
        {pkg.features.map((feature, idx) => (
          <div key={idx} className="flex items-start gap-3 mb-2 md:mb-3">
            <Check size={16} color="#F97316" className="flex-shrink-0 mt-0.5" />
            <span className="text-xs md:text-sm text-white/70">{feature}</span>
          </div>
        ))}
      </div>

      <Link
        to="/contact"
        className={`w-full justify-center text-sm md:text-base inline-flex items-center gap-2 py-3 px-6 font-semibold transition-colors rounded-xl ${
          pkg.popular || isCustom
            ? 'bg-[#F97316] text-white hover:bg-[#EA580C]' 
            : 'bg-transparent border border-white/20 text-white hover:bg-white/10'
        }`}
      >
        {isCustom ? 'Contact Sales' : 'Get Started'}
        <ArrowRight size={16} />
      </Link>
    </div>
  );
};

export const PackagesPage = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('cloud');
  const [isAnnual, setIsAnnual] = useState(false);

  // Handle URL parameter for tab selection
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['cloud', 'cdn', 'deploy'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const tabs = [
    { id: 'cloud', name: 'Parler Cloud', icon: Cloud, color: '#2a5ff6' },
    { id: 'cdn', name: 'Parler CDN', icon: Globe, color: '#3B82F6' },
    { id: 'deploy', name: 'Parler Deploy', icon: Rocket, color: '#F97316' },
  ];

  const getAccentColor = () => {
    switch (activeTab) {
      case 'cdn': return '#3B82F6';
      case 'deploy': return '#F97316';
      default: return '#2a5ff6';
    }
  };

  const getDescription = () => {
    switch (activeTab) {
      case 'cdn':
        return 'Global content delivery with enterprise-grade security. WAF, DDoS protection, and image optimization included.';
      case 'deploy':
        return 'Deploy your applications with confidence. Git integration, preview deployments, and team collaboration built-in.';
      default:
        return 'All plans include Triton SmartOS, Edgecast CDN, and enterprise-grade security.';
    }
  };

  const getFooterNote = () => {
    switch (activeTab) {
      case 'cdn':
        return 'All CDN plans include global edge network, SSL certificates, and real-time analytics.';
      case 'deploy':
        return 'All Deploy plans include automatic HTTPS, continuous deployment, and rollback capabilities.';
      default:
        return 'All plans are powered by Triton Cloud and Open Compute. Payment in the form of OPT is accepted here.';
    }
  };

  return (
    <div className="grid-pattern bg-[var(--bg-primary)] min-h-screen pt-28 md:pt-36">
      {/* Header */}
      <section className="px-4 md:px-8 lg:px-12 pb-8 md:pb-12">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold mb-4 md:mb-6">
            Choose Your <span className="text-blue-gradient">Package</span>
          </h1>
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto mb-2">
            Transparent pricing with no hidden fees* across all our products.
          </p>
          <div className="flex items-center justify-center gap-3 text-sm md:text-base text-white mb-8 md:mb-10">
            <img 
              src="/optio-token.png" 
              alt="OPT Token" 
              className="w-6 h-6 token-spin"
            />
            <span>OPT accepted for all plans below.</span>
          </div>

          {/* Product Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-6 md:mb-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 text-sm md:text-base font-semibold transition-all rounded-xl ${
                    activeTab === tab.id 
                      ? 'text-white' 
                      : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                  style={activeTab === tab.id ? { backgroundColor: tab.color } : {}}
                >
                  <Icon size={18} />
                  <span className="hidden sm:inline">{tab.name}</span>
                  <span className="sm:hidden">{tab.name.split(' ')[1]}</span>
                </button>
              );
            })}
          </div>

          <p className="text-sm md:text-base text-white/60 max-w-xl mx-auto">
            {getDescription()}
          </p>
        </div>
      </section>

      {/* Billing Toggle */}
      <BillingToggle isAnnual={isAnnual} setIsAnnual={setIsAnnual} />

      {/* Packages Grid */}
      <section className="px-4 md:px-8 lg:px-12 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto">
          {/* Cloud Packages */}
          {activeTab === 'cloud' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
              {cloudPackages.map((pkg) => (
                <CloudPackageCard key={pkg.id} pkg={pkg} isAnnual={isAnnual} />
              ))}
            </div>
          )}

          {/* CDN Packages */}
          {activeTab === 'cdn' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
              {cdnPackages.map((pkg) => (
                <CDNPackageCard key={pkg.id} pkg={pkg} isAnnual={isAnnual} />
              ))}
            </div>
          )}

          {/* Deploy Packages */}
          {activeTab === 'deploy' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
              {deployPackages.map((pkg) => (
                <DeployPackageCard key={pkg.id} pkg={pkg} isAnnual={isAnnual} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer Note */}
      <section className="px-4 md:px-8 lg:px-12 pb-8 md:pb-12">
        <div 
          className="max-w-3xl mx-auto text-center p-6 md:p-8 bg-[var(--bg-secondary)] border rounded-2xl"
          style={{ borderColor: `${getAccentColor()}33` }}
        >
          <p className="text-sm md:text-base text-white/70">
            {getFooterNote()}
          </p>
        </div>
      </section>

      {/* Pricing Disclaimer */}
      <section className="px-4 md:px-8 lg:px-12 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs text-white/40 text-center leading-relaxed">
            *Prices exclude applicable taxes, regulatory charges, and third-party pass-through fees.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PackagesPage;
